import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import c from 'classnames';
import _ from 'lodash';

import { Toast, Popover } from '@miotech/mprod';

import {
  W,
  Avatar,
  user,
  Analytics,
  redirectToApplication,
  getUrlToApplication,
  hasPermission,
} from '../';

import { UserLanguage } from '../types';
import MioNavLinkButton from './MioNavLinkButton/MioNavLinkButton';
import './MainNavigationBarNext.styl';

const RETRIEVE_PREFERENCE = '/api/user/preference/retrieve';
const RETRIEVE_LOCALE_LIST = '/api/locale/retrieve';
const UPDATE_PREFERENCE = '/api/user/preference/save';

function UserMenu({
  showMenu,
  onChangeShowMenu,
}: {
  showMenu: boolean;
  onChangeShowMenu: (state: boolean) => void;
}) {
  const [languageList, setLanguageList] = useState([]);
  const [currency, setCurrency] = useState();
  const [currentLanguage, setCurrentLanguage] = useState('en-US');

  const shouldCall = useRef(true);

  useEffect(() => {
    const handleHideDropdown = () => onChangeShowMenu(false);
    window.addEventListener('click', handleHideDropdown);
    return () => {
      window.removeEventListener('click', handleHideDropdown);
    };
  }, [onChangeShowMenu]);

  const retrievePreference = async () => {
    const json = await Analytics.post(RETRIEVE_PREFERENCE, {});
    if (json.code === 0 && shouldCall.current) {
      setCurrentLanguage(json.result.language);
      setCurrency(json.result.currency);
    }
  };

  const retrieveLocaleList = async () => {
    const json = await Analytics.post(RETRIEVE_LOCALE_LIST, {});
    if (json.code === 0 && shouldCall.current) {
      setLanguageList(json.result);
    }
  };

  const updatePreference = async (lang: UserLanguage) => {
    const json = await Analytics.post(UPDATE_PREFERENCE, {
      language: lang,
      currency,
    });
    if (json.code === 0 && shouldCall.current) {
      window.location.reload(false);
    }
  };

  useEffect(() => {
    retrievePreference();
    retrieveLocaleList();
    return () => {
      shouldCall.current = false;
    };
  }, []);

  const name = useMemo(() => {
    const _name = `${_.get(user, 'basicInfo.firstName', '')} ${_.get(
      user,
      'basicInfo.lastName',
      '',
    )}`;
    if (!_name.trim()) return '-';
    return _name;
  }, []);

  const email = useMemo(() => _.get(user, 'basicInfo.email', ''), []);

  const handleClickLogout = () => {
    Analytics.post('/api/user/logout')
      .then(response => {
        if (response.code === 0 || response.code === 27) {
          Toast.success('Log out success!');
          redirectToApplication('USER', '');
        } else {
          // Toast.error('Log out failed!');
        }
      })
      .catch(() => {
        // Toast.error('Log out failed!');
      });
  };

  const [showLangMenu, setShowLangMenu] = useState(false);

  const handleClickLangItem = (lang: UserLanguage) => {
    if (currentLanguage === lang) {
      return;
    }
    updatePreference(lang);
    setShowLangMenu(false);
  };

  const languageMenu = (
    <div className={c('languageMenu', { show: showLangMenu })}>
      {languageList.map(lang => (
        <div
          key={lang}
          className="dropdownItem"
          onClick={() => handleClickLangItem(lang)}
        >
          {W(`adm.d.fieldValue.locale.${lang}`)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="userMenu">
      <Avatar name={name} size="small" />
      <div
        className="dropdownButton"
        onClick={e => {
          e.stopPropagation();
          onChangeShowMenu(true);
        }}
      >
        <span className="email-name">
          <span className="name">{name}</span>
          <span className="email">{email}</span>
        </span>
        <i className={c('iconfont', 'icon-down', 'iconDown')} />
      </div>

      <div
        className="dropDown"
        style={{ display: showMenu ? 'block' : 'none' }}
      >
        <a
          href={`${getUrlToApplication('ADM')}/users/${_.get(
            user,
            'userId',
          )}?from=${window.location.href}`}
          className="dropdownItem"
        >
          {W('shared.mainNavNext.editProfile')}
        </a>

        <div
          className={c('dropdownItem')}
          onMouseEnter={() => setShowLangMenu(true)}
          onMouseLeave={() => setShowLangMenu(false)}
        >
          <span>{W('shared.mainNav.preference')}</span>
          <i
            className="iconfont icon-arrow_right"
            style={{ marginLeft: 'auto', fontSize: 12 }}
          />
          {languageMenu}
        </div>
        <div
          className={c('dropdownItem', 'logout')}
          onClick={handleClickLogout}
        >
          {W('shared.mainNavNext.logOut')}
        </div>
      </div>
    </div>
  );
}

UserMenu.propTypes = {
  showMenu: PropTypes.bool.isRequired,
  onChangeShowMenu: PropTypes.func.isRequired,
};

function NavigationBar(props: { children?: JSX.Element }) {
  const homepageUrl = _.get(window, '__config__.landingPageUrl', '#');

  const [dropdownMenuVisible, setdropdownMenuVisible] = useState(false);

  const handleDropdownMenuVisible = useCallback(v => {
    setdropdownMenuVisible(v);
  }, []);

  return (
    <div
      className="NavigationBarNext"
      style={{
        backgroundImage:
          'url(https://static.miotech.com/images/background/ami-bg-1920x320.jpg)',
      }}
    >
      {/* eslint-disable-next-line */}
      <a
        href={homepageUrl}
        className="logo"
        style={{
          // TODO: logo暂时写死, 之后还是需要改成接口返回
          // backgroundImage: `url("${user.companyLogoUrl}")`,
          backgroundImage:"url(https://resources.miotech.com/static/common/images/brand-logo/logo-m-af6b5d.svg)"
        }}
      />
      <div className="sepLine" />

      <div className="apps-item-container">
        {hasPermission('AMI') && (
          <div className="apps-item">
            <MioNavLinkButton
              className="apps-item-inner"
              activeClassName="is-active"
              appKey="AMI"
            >
              <img
                src="https://static.miotech.com/images/icons/ami-nav-513x160.svg"
                alt="AMI"
                className="apps-item-logo"
                style={{ height: '20px' }}
              />
            </MioNavLinkButton>
          </div>
        )}
        {/* {hasPermission('OPENAPI') && (
          <div className="apps-item">
            <MioNavLinkButton
              className="apps-item-inner"
              activeClassName="is-active"
              appKey="OPENAPI"
            >
              <img
                src="https://static.miotech.com/images/icons/mioapi-488x80-dark.svg"
                alt="OpenAPI"
                className="apps-item-logo open-api"
              />
            </MioNavLinkButton>
          </div>
        )} */}
        {(hasPermission('SPREADI') || hasPermission('SPREADI2')) && (
          <div className="apps-item">
            <MioNavLinkButton
              className="apps-item-inner"
              activeClassName="is-active"
              appKey="SPREADI"
            >
              <img
                src="https://static.miotech.com/dark/icons/application-spreadi-rectangle-848x160.svg"
                alt="Spreadi"
                className="apps-item-logo"
              />
            </MioNavLinkButton>
          </div>
        )}
         {hasPermission('ESG_HUB') && (
          <div className="apps-item">
            <MioNavLinkButton
              className="apps-item-inner"
              activeClassName="is-active"
              appKey="ESG_HUB"
            >
              <img
                src="https://resources.miotech.com/static/landing-page/images/ESGhub-LP-kfya7e.svg"
                alt="ESG_HUB"
                className="apps-item-logo"
              />
            </MioNavLinkButton>
          </div>
        )}
        {hasPermission('CARBON_LENS') && (
          <div className="apps-item">
            <MioNavLinkButton
              className="apps-item-inner"
              activeClassName="is-active"
              appKey="CARBON_LENS"
            >
              <img
                src="https://resources.miotech.com/static/landing-page/images/carbon-lens-jgfu32.svg"
                alt="CARBON_LENS"
                className="apps-item-logo"
              />
            </MioNavLinkButton>
          </div>
        )}
      </div>

      {/* ami custom children */}
      {hasPermission('AMI') && props.children && (
        <div style={{ marginLeft: 'auto' }}>{props.children}</div>
      )}

      {hasPermission('SAAS_ADMIN') && (
        <Popover trigger="hover" content={W('shared.d.mainNavTitle.ADM')}>
          <a
            className={c('adminButton', {
              dropdownButton: !props.children,
            })}
            style={{ marginRight: 24 }}
            href={getUrlToApplication('ADM')}
          >
            <i className="iconfont icon-clients" />
          </a>
        </Popover>
      )}

      {hasPermission('ORG_ADMIN') && !hasPermission('SAAS_ADMIN') && (
        <Popover trigger="hover" content={W('shared.d.mainNavTitle.ADM')}>
          <a
            className={c('adminButton', {
              dropdownButton: !props.children,
            })}
            style={{ marginRight: 24 }}
            href={getUrlToApplication(
              'ADM',
              `/organization/${_.get(user, 'basicInfo.organization', '')}`,
            )}
          >
            <i className="iconfont icon-clients" />
          </a>
        </Popover>
      )}

      <span
        className={c({
          dropdownButton:
            !hasPermission(['SAAS_ADMIN', 'ORG_ADMIN']) && !props.children,
        })}
      >
        <UserMenu
          showMenu={dropdownMenuVisible}
          onChangeShowMenu={handleDropdownMenuVisible}
        />
      </span>
    </div>
  );
}

export default NavigationBar;
